// oxlint-disable-next-line no-restricted-imports
import { Box, Group, Stack } from "@mantine/core";
import { IconFilePlus } from "@tabler/icons-react";
import React, { useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { colors } from "#app/theme/v2/colors";
import { radius } from "#app/theme/v2/radius";
import { spacing } from "#app/theme/v2/spacing";
import { Button } from "#components/atoms/Button/Button";
import { Divider } from "#components/atoms/Divider/Divider";
import { Text } from "#components/atoms/Typographies/Text";

import { FileUploadRow } from "./FileUploadRow";

type FileUploadFile = {
  progress: number;
  name: string;
  hash: string;
};

type FileUploadProps = {
  files: FileUploadFile[];
  onAdd: (files: FileList) => void;
  onDelete: (fileHash: string) => void;
  accept?: string;
  label?: string;
};

const FileUpload = ({ files, accept, label, onAdd, onDelete }: FileUploadProps) => {
  const { t } = useTranslation("components");
  const inputRef = useRef<HTMLInputElement>(null);
  const [isDragging, setIsDragging] = useState(false);

  const openFilePicker = () => inputRef.current?.click();

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files) {
      return;
    }
    onAdd(e.target.files);
    e.target.value = "";
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    onAdd(e.dataTransfer.files);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    if (!e.currentTarget.contains(e.relatedTarget as Node)) {
      setIsDragging(false);
    }
  };

  const isEmpty = files.length === 0;

  return (
    <Stack gap={spacing[1]}>
      {label && (
        <Text size="small" type="medium">
          {label}
        </Text>
      )}
      <Box
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        style={{
          background: isDragging ? colors.bg.elevation.tertiary : colors.bg.elevation.secondary,
          border: `1px dashed ${isDragging ? colors.border.primaryDefault : colors.border.secondaryDefault}`,
          borderRadius: radius.sm,
          padding: isEmpty ? `${spacing[12]} ${spacing[6]}` : spacing[6],
          width: "100%",
          transition: "background 0.15s ease, border-color 0.15s ease",
        }}
      >
        <input
          ref={inputRef}
          type="file"
          multiple
          hidden
          accept={accept}
          onChange={handleFileChange}
        />

        {isEmpty ? (
          <Stack align="center" gap={spacing[3]}>
            <Stack align="center" gap={spacing[3]}>
              <IconFilePlus size={32} color={colors.icon.tertiary} strokeWidth={1.5} />
              <Text type="medium" textAlign="center">
                {t("fileUpload.dropzoneTitle")}
              </Text>
              <Stack gap={0} align="center">
                <Text size="small" color={colors.text.secondaryDefault} textAlign="center">
                  {t("fileUpload.dropzoneDescription")}
                </Text>
              </Stack>
            </Stack>
            <Button variant="primary" onClick={openFilePicker}>
              {t("fileUpload.browse")}
            </Button>
          </Stack>
        ) : (
          <Stack gap={spacing[4]}>
            <Group justify="space-between" align="center">
              <Text size="small" color={colors.text.secondaryDefault}>
                {t("fileUpload.fileCount", { count: files.length })}
              </Text>
              <Button variant="tertiary" size="sm" onClick={openFilePicker}>
                {t("fileUpload.addFiles")}
              </Button>
            </Group>
            <Box style={{ maxHeight: "246px", overflowY: "auto", scrollbarGutter: "stable" }}>
              {files.map((file, index) => (
                <React.Fragment key={file.hash}>
                  <FileUploadRow file={file} onDelete={onDelete} />
                  {index < files.length - 1 && <Divider />}
                </React.Fragment>
              ))}
            </Box>
          </Stack>
        )}
      </Box>
    </Stack>
  );
};

export { FileUpload };
export type { FileUploadFile };
